/** Stable backend code when X-Trove-Aid is not in user membership. */
export const WORKSPACE_ACCESS_DENIED_CODE = 'WORKSPACE_ACCESS_DENIED';
/** Backend entitlement / billing error codes surfaced as `{ status: "error", code }`. */
export const PLAN_FEATURE_DISABLED_CODE = 'PLAN_FEATURE_DISABLED';
export const QUOTA_EXCEEDED_CODE = 'QUOTA_EXCEEDED';
export const ACCOUNT_READ_ONLY_CODE = 'ACCOUNT_READ_ONLY';
export const PUBLIC_API_QUOTA_EXCEEDED_CODE = 'PUBLIC_API_QUOTA_EXCEEDED';
const FEATURE_LABELS_EN = {
    publicShareLinks: 'Public share links',
    collaboration: 'Team collaboration',
    dataExport: 'Data export',
    dataImport: 'Data import',
    byok: 'Bring your own API key (BYOK)',
    aiYoutube: 'YouTube summarization',
    aiBookmarkContext: 'Bookmark context AI',
    kanbanViews: 'Kanban views',
    prioritySupport: 'Priority support',
    ssoSaml: 'SSO / SAML sign-in',
    auditLog: 'Audit log',
    publicApiRead: 'Public API read access',
    publicApiWrite: 'Public API write access',
    webhooks: 'Webhooks',
    e2eWorkspaces: 'End-to-end encrypted workspaces',
    workMemoryAi: 'Work Memory AI',
    bookmark: 'bookmarks',
    notes: 'notes',
    users: 'team seats',
    highlighter: 'highlights',
    sessionbookmarkgroup: 'bookmark groups',
    tags: 'tags',
    taskstag: 'task tags',
    groups: 'folders',
    tasks: 'tasks',
    workspace: 'workspaces',
    smartCollections: 'smart collections',
    apiCallsPerMonth: 'API calls',
    ai_search: 'AI search',
    ai_assistant: 'AI assistant',
    ai_suggest: 'AI suggestions',
    ai_summarize: 'AI summarize',
    ai_resurface: 'library resurfacing',
};
const PLAN_LABELS_EN = {
    free: 'Free',
    pro: 'Pro',
    team: 'Team',
    business: 'Business',
    plus: 'Pro',
};
export function getAxiosErrorResponseData(error) {
    if (error == null || typeof error !== 'object' || !('response' in error))
        return undefined;
    return error.response?.data;
}
export function isAxiosGenericStatusMessage(message) {
    return /^Request failed with status code \d{3}$/i.test(message.trim());
}
export function parseStructuredApiError(data) {
    if (data == null || typeof data !== 'object')
        return null;
    const o = data;
    const status = String(o.status ?? '').toLowerCase();
    if (status !== 'error' && status !== 'fail')
        return null;
    const code = o.code;
    if (typeof code !== 'string' || code.trim() === '')
        return null;
    return {
        code,
        message: typeof o.message === 'string' ? o.message : undefined,
        feature: typeof o.feature === 'string' ? o.feature : undefined,
        upgradeTo: typeof o.upgradeTo === 'string' ? o.upgradeTo : undefined,
        used: typeof o.used === 'number' ? o.used : undefined,
        max: typeof o.max === 'number' ? o.max : undefined,
    };
}
function featureLabelEn(feature) {
    if (!feature)
        return 'This feature';
    return FEATURE_LABELS_EN[feature] ?? feature.replace(/([A-Z_])/g, ' $1').trim().toLowerCase();
}
function planLabelEn(plan) {
    if (!plan)
        return 'a paid plan';
    return PLAN_LABELS_EN[plan] ?? plan;
}
/**
 * User-facing copy for structured API errors. Used by the Axios interceptor so
 * existing `err.message` toasts are readable without per-callsite changes.
 */
export function humanizeApiErrorBody(data) {
    const parsed = parseStructuredApiError(data);
    if (!parsed) {
        const msg = getMessageFromResponse(data);
        if (!msg || isAxiosGenericStatusMessage(msg))
            return undefined;
        if (msg.includes('requires a higher plan'))
            return undefined;
        return msg;
    }
    const feature = featureLabelEn(parsed.feature);
    const plan = planLabelEn(parsed.upgradeTo);
    switch (parsed.code) {
        case PLAN_FEATURE_DISABLED_CODE:
            return `${feature} is not included on your current plan. Upgrade to ${plan} to use it.`;
        case QUOTA_EXCEEDED_CODE:
            if (typeof parsed.used === 'number' && typeof parsed.max === 'number') {
                return `You've reached your ${feature} limit (${parsed.used} / ${parsed.max}). Upgrade your plan for more.`;
            }
            return `You've reached your ${feature} limit. Upgrade your plan for more.`;
        case ACCOUNT_READ_ONLY_CODE:
            return 'Your account is read-only until billing is updated. You can still view and export your data.';
        case PUBLIC_API_QUOTA_EXCEEDED_CODE:
            return 'Your public API monthly quota is used up. Upgrade your plan or wait until the next billing month.';
        case WORKSPACE_ACCESS_DENIED_CODE:
            return parsed.message ?? 'You no longer have access to this workspace.';
        case 'ACCOUNT_ADMIN_REQUIRED':
            return parsed.message ?? 'Only an account admin can perform this action.';
        case 'ACCOUNT_OWNER_REQUIRED':
            return parsed.message ?? 'Only the account owner can perform this action.';
        default:
            if (parsed.message && !parsed.message.includes('requires a higher plan')) {
                return parsed.message;
            }
            return undefined;
    }
}
export function attachHumanApiErrorMessage(error) {
    if (!error || typeof error !== 'object')
        return;
    const data = getAxiosErrorResponseData(error);
    const human = humanizeApiErrorBody(data);
    if (!human)
        return;
    const err = error;
    err.message = human;
    const structured = parseStructuredApiError(data);
    if (structured)
        err.troveApi = structured;
}
export function getResponseCode(data) {
    if (data == null || typeof data !== 'object')
        return undefined;
    const code = data.code;
    return typeof code === 'string' ? code : undefined;
}
export function getDefaultAidFromResponse(data) {
    if (data == null || typeof data !== 'object')
        return undefined;
    const defaultAid = data.defaultAid;
    return typeof defaultAid === 'string' && defaultAid.trim() !== '' ? defaultAid : undefined;
}
export function isWorkspaceAccessDeniedResponse(data) {
    return getResponseCode(data) === WORKSPACE_ACCESS_DENIED_CODE;
}
/** Backend message indicating email is not verified (substring match) */
const EMAIL_NOT_VERIFIED_MESSAGE_MARKER = 'not verified';
export const isAuthFailureStatus = (status) => status === 401 || status === 403;
/** True when the server responded with 401/403 (session invalid / forbidden). Not timeouts or network errors. */
export function isAxiosUnauthorizedError(error) {
    const status = error?.response?.status;
    return isAuthFailureStatus(status);
}
/**
 * Set when a 401/403 retry failed because Firebase token refresh hit a transient error
 * (e.g. auth/network-request-failed). Callers must not sign the user out in this case.
 */
export function isTransientAuthFailure(error) {
    return Boolean(error &&
        typeof error === 'object' &&
        error.transientAuthFailure === true);
}
/** Extract message from various response shapes (object.message, object.error.message, or string body) */
export function getMessageFromResponse(data) {
    if (data == null)
        return undefined;
    if (typeof data === 'string')
        return data;
    if (typeof data !== 'object')
        return undefined;
    const o = data;
    const msg = o.message ??
        (o.error && typeof o.error === 'object' && o.error.message) ??
        o.msg;
    return typeof msg === 'string' ? msg : undefined;
}
/** Returns true if the API response body indicates "email not verified" */
export function isEmailNotVerifiedResponse(data) {
    const msg = getMessageFromResponse(data);
    return typeof msg === 'string' && msg.toLowerCase().includes(EMAIL_NOT_VERIFIED_MESSAGE_MARKER);
}
/** Returns true if the message string indicates "email not verified" (e.g. from PrefAppError) */
export function isEmailNotVerifiedMessage(message) {
    return message.toLowerCase().includes(EMAIL_NOT_VERIFIED_MESSAGE_MARKER);
}
/** Copy that should still run the auth-expired / retry flow (not app-level validation). */
function isLikelyAuthFailureMessageText(msg) {
    const lower = msg.toLowerCase();
    if (lower.includes('unauthorized'))
        return true;
    if (lower.includes('invalid token'))
        return true;
    if (lower.includes('token expired'))
        return true;
    if (lower.includes('jwt'))
        return true;
    if (lower.includes('session expired'))
        return true;
    if (lower.includes('authentication failed'))
        return true;
    return false;
}
/**
 * Backend uses `{ status: "error", message }` for both auth failures and application errors
 * (e.g. Firestore "Update() requires…"). On 401/403, do not sign out when the message is clearly
 * not an auth/token/session problem.
 */
export function isBackendApplicationErrorEnvelope(data) {
    const structured = parseStructuredApiError(data);
    if (structured) {
        switch (structured.code) {
            case PLAN_FEATURE_DISABLED_CODE:
            case QUOTA_EXCEEDED_CODE:
            case ACCOUNT_READ_ONLY_CODE:
            case PUBLIC_API_QUOTA_EXCEEDED_CODE:
            case WORKSPACE_ACCESS_DENIED_CODE:
            case 'ACCOUNT_ADMIN_REQUIRED':
            case 'ACCOUNT_OWNER_REQUIRED':
                return true;
            default:
                break;
        }
    }
    if (data == null || typeof data !== 'object')
        return false;
    const o = data;
    if (o.status !== 'error' && o.status !== 'fail')
        return false;
    const msg = getMessageFromResponse(data);
    if (typeof msg !== 'string' || msg.length === 0)
        return false;
    if (isLikelyAuthFailureMessageText(msg))
        return false;
    if (isEmailNotVerifiedResponse(data))
        return false;
    return true;
}
